import type { Dict } from './types';

/**
 * RepCheck arayüz sözlüğü.
 *
 * Kural: `en` ve `tr` anahtar kümeleri birebir aynı olmalı (i18n testi bunu
 * doğrular). Motorların ürettiği Türkçe metinler `issues.ts` içinde İngilizce
 * şablonlarla eşlenir; bu sözlük yalnızca arayüz metinlerini taşır.
 */

const en: Dict = {
	// ---------------------------------------------------------------- common
	'common.beta': 'beta',
	'common.language': 'Language',
	'common.close': 'Close',
	'common.weight': 'weight {n}%',
	'common.page': 'p. {n}',
	'common.line': 'line {n}',
	'common.chars': 'characters',
	'common.hide': 'Hide',
	'common.show': 'Show',
	'common.yes': 'yes',
	'common.no': 'no',
	'severity.critical': 'Critical',
	'severity.warning': 'Warning',
	'severity.info': 'Info',
	'severity.ok': 'OK',
	'severity.critical.hint': 'A strongly evidenced finding that directly affects the verdict',
	'severity.warning.hint': 'A reporting problem that should be fixed',
	'severity.info.hint': 'A style or preference note',
	'severity.ok.hint': 'No problem detected',
	'category.statistics': 'Statistical reporting',
	'category.citations': 'Citations and references',
	'category.structure': 'Structural integrity',
	'category.language': 'Language and claim strength',
	'category.provenance': 'Extraction confidence',
	'category.short.statistics': 'Statistics',
	'category.short.citations': 'Citations',
	'category.short.structure': 'Structure',
	'category.short.language': 'Language',
	'category.short.provenance': 'Extraction',
	'category.notChecked': 'not checked',
	'category.findings': '{n} findings',
	'category.criticals': '{n} critical',
	'category.noData': 'no data',
	'confidence.high': 'High confidence',
	'confidence.medium': 'Medium confidence',
	'confidence.low': 'Low confidence',
	'confidence.high.short': 'high',
	'confidence.medium.short': 'medium',
	'confidence.low.short': 'low',
	'design.experimental': 'Experimental',
	'design.quasi': 'Quasi-experimental',
	'design.correlational': 'Correlational / cross-sectional',
	'design.case': 'Case study',
	'design.unknown': 'Not determined',
	'time.justNow': 'just now',
	'time.minutesAgo': '{n} min ago',
	'time.hoursAgo': '{n} h ago',
	'reason.decimalComma': 'Decimal comma used; Turkish number format.',
	'reason.pMissing': 'No p-value was reported in the same expression.',
	'reason.dfMissingT': 'No degrees of freedom for t; p cannot be recomputed.',
	'reason.dfMissingF': 'Degrees of freedom are missing for F.',
	'reason.dfMissingChi': 'No degrees of freedom for χ².',
	'reason.nMissing': 'No sample size for r.',
	'reason.negativeT': 'Negative t value; the direction of the comparison is unclear.',
	'reason.impossible': 'The value lies outside the possible range.',
	'reason.operatorLoss': 'Possible operator loss in the p expression ("p , .0001").',
	'reason.pOverOne': 'The p-value was read as greater than 1.',
	'field.psikoloji': 'psychology',
	'field.eğitim': 'education',
	'field.sosyal bilim': 'social science',
	'field.sağlık': 'health',

	// --------------------------------------------------------------- landing
	'meta.title': 'RepCheck — manuscript reporting audit',
	'meta.description':
		'RepCheck audits statistical reporting, citations, structure and claim language in manuscripts — entirely in your browser.',
	'brand.homeAria': 'RepCheck home',
	'nav.paste': 'Paste text',
	'nav.testSet': 'Test set',
	'hero.kicker': 'Runs in your browser · files never leave your device',
	'hero.titleA': 'Are your manuscript’s',
	'hero.titleEm': 'numbers',
	'hero.titleB': 'internally consistent?',
	'hero.ledeA':
		'RepCheck recomputes the statistical claims in your PDF, Word or LaTeX manuscript, cross-checks in-text citations against the reference list, and audits structure and claim language. Every finding carries a',
	'hero.ledeStrong': 'confidence score',
	'hero.ledeB': '— low-confidence findings never count toward the verdict.',
	'demo.quickLabel': 'Try it now:',
	'demo.button': 'Analyze the sample article',
	'demo.note': 'a demo article with deliberately planted errors',
	'demo.cta': 'Try the demo',
	'demo.ownCta': 'Try your own manuscript',
	'error.title': 'The document could not be processed',
	'error.retry': 'Try again',
	'error.demo': 'Continue with the demo',
	'paste.title': 'Paste text',
	'paste.hint': 'Paste the manuscript text you copied. At least 100 characters are required.',
	'paste.fileName': 'pasted-text',
	'paste.fileNameTxt': 'pasted-text.txt',
	'paste.placeholder': 'Abstract\nThis study ... t(48) = 2.41, p = .019 ...',
	'paste.chars': '{n} characters',
	'paste.analyze': 'Analyze',
	'checks.title': 'Five audit layers',
	'checks.subtitle': 'Each layer is scored separately; the overall score is a weighted average.',
	'checks.weight': 'weight {n}%',
	'checks.stat.title': 'Statistics',
	'checks.stat.body': 'Parses t, F, χ², r and p; recomputes p-values with jStat.',
	'checks.cite.title': 'Citations',
	'checks.cite.body': 'Matches in-text citations to the reference list; flags missing and uncited entries.',
	'checks.struct.title': 'Structure',
	'checks.struct.body': 'Checks IMRaD sections, table/figure captions and their in-text references.',
	'checks.lang.title': 'Language',
	'checks.lang.body': 'Weighs causal language against the study design; flags overclaims.',
	'checks.prov.title': 'Provenance',
	'checks.prov.body': 'Reports scanned pages, broken glyphs and operator loss; adjusts confidence accordingly.',
	'sample.kicker': 'Demo',
	'sample.title': 'A sample article with planted errors',
	'sample.bodyA':
		'A fictional article on social media and academic procrastination: a p-value contradiction, operator loss (',
	'sample.bodyB':
		'), a citation missing from the references, an uncited table, and causal language. See how the result is reported.',
	'sample.open': 'Open the sample',
	'privacy.title': 'Private by design',
	'privacy.body':
		'Your document never leaves the browser. PDF parsing, p-value recomputation and report generation all run locally. Close the tab and the memory is gone; no copy is stored anywhere.',
	'privacy.li1': 'No server, no account, no telemetry.',
	'privacy.li2': 'PDF text is extracted in a Web Worker; the interface never freezes.',
	'privacy.li3': 'Export reports as HTML, CSV or JSON.',
	'footer.tagline': 'RepCheck · a client-side pre-submission audit tool',
	'footer.note': 'Not a peer-review verdict; low-confidence findings need manual verification.',

	// -------------------------------------------------------------- dropzone
	'drop.aria': 'Upload a manuscript: PDF, DOCX or LaTeX',
	'drop.headline': 'Drop your manuscript here',
	'drop.subBefore': 'or',
	'drop.subLink': 'choose a file',
	'drop.subAfter': '— PDF, Word (.docx), LaTeX (.tex) or plain text',
	'drop.limit': 'up to 40 MB',
	'drop.errorUnsupported':
		'This file type is not supported. Upload a PDF, DOCX, .tex or .txt file. (Legacy .doc files must be saved as .docx in Word.)',
	'drop.errorSize': 'The file is larger than 40 MB. Try a smaller file.',
	'drop.errorEmpty': 'The file appears to be empty.',

	// ------------------------------------------------------------ processing
	'proc.file': 'Text extraction',
	'proc.analyze': 'Engines running',
	'proc.report': 'Report',
	'proc.local': 'in-browser, no server',
	'proc.evaluating': 'evaluating findings',
	'proc.pageOf': 'page {page} of {total}',
	'proc.stage.starting': 'Starting',
	'proc.stage.preparing': 'Preparing the file',
	'proc.stage.pdfReading': 'Reading the PDF',
	'proc.stage.pdfOpened': 'PDF opened',
	'proc.stage.page': 'Reading page {page} of {total}',
	'proc.stage.workerFallback': 'Worker unavailable; trying on the main thread',
	'proc.stage.docxLoader': 'Loading the DOCX reader',
	'proc.stage.docxReading': 'Reading the DOCX',
	'proc.stage.paragraphs': 'Extracting paragraphs',
	'proc.stage.latexRead': 'Reading the LaTeX source',
	'proc.stage.latexParse': 'Parsing LaTeX',
	'proc.stage.textRead': 'Reading text',
	'proc.stage.analyzing': 'Analyzing the text',
	'proc.stage.report': 'Building the report',
	'proc.stage.demoPreparing': 'Preparing the demo article',
	'proc.stage.done': 'Done',

	// ----------------------------------------------------------- report shell
	'report.back': 'New document',
	'report.quality': 'extraction confidence: {level}',
	'report.qualityHigh': 'high',
	'report.qualityMedium': 'medium',
	'report.qualityLow': 'low',
	'report.exportHtml': 'HTML report',
	'report.tab.overview': 'Overview',
	'report.tab.findings': 'Findings',
	'report.tab.stats': 'Statistics',
	'report.tab.refs': 'Citations',
	'report.tab.structure': 'Structure & language',
	'report.tab.method': 'Method',
	'report.hideText': 'Hide text',
	'report.showText': 'Show text',
	'report.charsK': '{n}k characters',
	'report.tabsAria': 'Report sections',

	// -------------------------------------------------------------- overview
	'overview.eyebrow': 'Overall assessment',
	'overview.critical': '{n} critical',
	'overview.warning': '{n} warnings',
	'overview.info': '{n} notes',
	'overview.undecided':
		'{n} findings were excluded from the verdict because of low confidence; they need manual verification.',
	'overview.verdict.clean':
		'<strong>Reporting is largely consistent.</strong> No critical contradiction was found; only minor improvements are suggested.',
	'overview.verdict.critical':
		'<strong>{n} critical finding(s)</strong> require the statistical reporting or the reference list to be verified. Do not submit before confirming them against the raw data.',
	'overview.verdict.good':
		'<strong>Generally good.</strong> A few reporting gaps remain; they can be fixed quickly.',
	'overview.verdict.medium':
		'<strong>Moderate issues.</strong> Several warnings co-occur; the reporting standard should be reviewed.',
	'overview.verdict.bad':
		'<strong>Serious reporting problems.</strong> Overall score {score} (grade {grade}); findings must be addressed one by one.',
	'overview.qualityEyebrow': 'Text extraction confidence',
	'overview.qualityOk': 'The text layer looks healthy; findings rest on the extracted text.',
	'overview.categoriesTitle': 'Category scores',
	'overview.categoriesSub': 'Scores are reduced only by high/medium-confidence findings.',
	'overview.priorityTitle': 'Priority findings',
	'overview.prioritySub': 'The {n} most important findings that count toward the verdict.',
	'overview.priorityNone': 'No critical or warning-level finding counts toward the verdict.',
	'overview.coverageTitle': 'Coverage',
	'overview.covStats': 'Statistical expressions',
	'overview.covStatsNote': '{consistent} consistent · {inconsistent} contradictory',
	'overview.covCitations': 'In-text citations',
	'overview.covCitationsNote': '{matched} matched · {missing} missing',
	'overview.covReferences': 'Reference entries',
	'overview.covReferencesNote': '{orphans} uncited',
	'overview.covFigures': 'Tables / figures',
	'overview.covFiguresNote': '{unreferenced} unreferenced',
	'overview.covLanguage': 'Language flags',
	'overview.covLanguageNote': '{causal} unjustified causal',
	'overview.design': 'Study design:',

	// -------------------------------------------------------------- findings
	'findings.all': 'All',
	'findings.category': 'Category',
	'findings.searchLabel': 'Search',
	'severity.label': 'Severity',
	'findings.critical': 'Critical',
	'findings.warning': 'Warning',
	'findings.info': 'Info',
	'findings.categoryAll': 'All categories',
	'findings.search': 'Search findings…',
	'findings.includeUndecided': 'Include findings excluded from the verdict',
	'findings.showing': '{n} findings shown',
	'findings.showingUndecided': ' · {n} excluded from the verdict',
	'findings.empty': 'No findings match this filter.',
	'findings.none': 'No findings.',
	'findings.legend': 'Severity legend',
	'findings.legendText': '{label}: {hint}',

	// ------------------------------------------------------------- issue card
	'issue.notDecisive': 'not counted in verdict',
	'issue.suggestion': 'Suggestion',
	'issue.showInText': 'Show in text',
	'issue.confidenceTitle': 'Confidence: {level}',
	'issue.lowConfidence': 'low confidence',

	// ----------------------------------------------------------------- stats
	'stats.total': 'statistical expressions',
	'stats.consistent': 'consistent',
	'stats.inconsistent': 'contradiction / deviation',
	'stats.unverifiable': 'unverifiable',
	'stats.empty':
		'No statistical expression was found. The tool looks for patterns such as t(48) = 2.41, p = .019.',
	'stats.emptyMono': 't(48) = 2.41, p = .019',
	'stats.col.expression': 'Expression',
	'stats.col.value': 'Value',
	'stats.col.df': 'df / n',
	'stats.col.reported': 'Reported p',
	'stats.col.computed': 'Computed p',
	'stats.col.status': 'Status',
	'stats.col.confidence': 'Confidence',
	'stats.status.consistent': 'Consistent',
	'stats.status.flip': 'Contradiction',
	'stats.status.deviation': 'Deviation',
	'stats.status.unverifiable': 'Unverifiable',
	'stats.status.uncomputable': 'Not computable',
	'stats.delta': 'Difference:',
	'stats.boundNote': 'the reported value is a bound (p < .001)',
	'stats.showInText': 'Show in text',
	'stats.claimsTitle': 'Significance claims',
	'stats.claimsHint':
		'Whether “significant” statements carry numerical support in the same sentence.',
	'stats.supported': 'supported',
	'stats.unsupported': 'unsupported',
	'stats.negated': 'negated',
	'stats.methodTitle': 'How are p-values recomputed?',
	'stats.methodT': 't → Student t distribution, df from t(·), two-tailed.',
	'stats.methodF': 'F → central F distribution, F(df1, df2), upper tail.',
	'stats.methodChi': 'χ² → chi-square distribution, χ²(df), upper tail.',
	'stats.methodR': 'r → t = r√((n−2)/(1−r²)), df = n−2, two-tailed.',
	'stats.methodZ': 'z → standard normal distribution, two-tailed.',
	'stats.tolerance':
		'Tolerance: 0.3 log₁₀ units (about 2×) or 0.005 absolute. Larger differences are “deviations”; differences that cross the significance threshold are “contradictions”. Low-confidence expressions are never reported as verdicts, even when they contradict.',
	'stats.notComputable': 'not computable',
	'stats.twoTailed': 'two-tailed',
	'stats.oneTailed': 'one-tailed',
	'stats.pShort': 'p = {p}',

	// ------------------------------------------------------------ references
	'refs.citations': 'in-text citations',
	'refs.references': 'reference entries',
	'refs.matched': 'matched',
	'refs.missing': 'missing from references',
	'refs.orphans': 'uncited',
	'refs.noSectionTitle': 'Reference section not found.',
	'refs.noSectionBody':
		'No heading such as “References”, “Bibliography” or “Kaynakça” was found, so citations could not be cross-checked.',
	'refs.noSectionCitations': '{n} citations exist in the text but could not be verified.',
	'refs.tab.matches': 'Matches',
	'refs.tab.missing': 'Missing / mismatched',
	'refs.tab.orphans': 'Uncited references',
	'refs.col.citation': 'In-text citation',
	'refs.col.status': 'Status',
	'refs.col.match': 'Reference match',
	'refs.status.matched': 'matched',
	'refs.status.missing': 'not in references',
	'refs.status.year': 'year mismatch',
	'refs.status.author': 'author mismatch',
	'refs.similar': 'Similar entries:',
	'refs.emptyMatches': 'No in-text citations found.',
	'refs.emptyMissing': 'Every in-text citation matches the reference list.',
	'refs.emptyOrphans': 'Every reference entry is cited in the text.',
	'refs.uncited': 'not cited in text',
	'refs.showInText': 'Show in text',
	'refs.etal': 'et al.',
	'refs.viewAria': 'Citation view',
	'refs.yearArrow': 'year: {from} → {to}',
	'refs.methodTitle': 'How does matching work?',
	'refs.methodBody':
		'In-text citations (Yılmaz, 2019; (Smith & Jones, 2020); Öztürk et al., 2021) are reduced to a surname + year key. Reference entries are parsed the same way. Surnames are compared with Jaro-Winkler similarity (threshold 0.86); a year that does not match exactly is reported as a “year mismatch”, and a surname that does not match as an “author mismatch”. Unmatched citations are critical; unused references are warnings.',

	// ------------------------------------------------------------- structure
	'struct.sectionsTitle': 'Section map',
	'struct.sectionsSub': 'The IMRaD structure is expected; headings may be numbered or plain.',
	'struct.section.abstract': 'Abstract',
	'struct.section.keywords': 'Keywords',
	'struct.section.introduction': 'Introduction',
	'struct.section.method': 'Method',
	'struct.section.results': 'Results',
	'struct.section.discussion': 'Discussion',
	'struct.section.references': 'References',
	'struct.section.appendix': 'Appendix',
	'struct.found': 'found',
	'struct.notFound': 'not found',
	'struct.detail': 'Details',
	'struct.gaps': 'Numbering gaps:',
	'struct.figuresTitle': 'Tables and figures',
	'struct.figuresSub': 'Caption ↔ in-text reference cross-check.',
	'struct.tables': 'Tables',
	'struct.figures': 'Figures',
	'struct.tableNo': 'Table {n}',
	'struct.figureNo': 'Figure {n}',
	'struct.referenced': 'referenced',
	'struct.unreferenced': 'unreferenced',
	'struct.noFigures': 'No table or figure caption was found.',
	'struct.dangling': 'Referenced in text but no caption:',
	'struct.languageTitle': 'Language and claim strength',
	'struct.languageSub':
		'Causal language, overclaims and significance claims without a test. Study design:',
	'struct.designEvidence': 'evidence:',
	'struct.emptyLang': 'No notable language pattern was found.',
	'struct.lang.causal': 'causal',
	'struct.lang.causalJustified': 'causal (supported by design)',
	'struct.lang.absolutist': 'overclaim',
	'struct.lang.generalization': 'generalization',
	'struct.lang.significance': 'unsupported significance',

	// -------------------------------------------------------------- method
	'method.title': 'How it works',
	'method.leadA':
		'RepCheck processes your manuscript',
	'method.leadStrong': 'entirely in your browser',
	'method.leadB':
		'. The file is never sent to a server; refreshing the page clears it from memory. The goal is to surface reporting inconsistencies an editor could catch before submission — an',
	'method.leadEm': 'audit, not a verdict',
	'method.pipe.extract': 'Extraction',
	'method.pipe.parse': 'Parsing',
	'method.pipe.cross': 'Cross-check',
	'method.pipe.confidence': 'Confidence filter',
	'method.pipe.score': 'Score + annotation',
	'method.pipelineAria': 'Pipeline',
	'method.confColLevel': 'Confidence',
	'method.confColCond': 'Typical condition',
	'method.confColEffect': 'Effect on score',
	'method.extractTitle': '1 · Text extraction',
	'method.extractPdf': 'PDF — pdf.js, in a separate Web Worker, page by page. Every text line keeps its bounding box.',
	'method.extractDocx': 'DOCX — mammoth.js returns raw paragraph text; there is no page geometry.',
	'method.extractTex': 'LaTeX — parsed from source; section, table/figure and reference structure is most reliable here.',
	'method.operatorNote':
		'Operator loss is a real problem in PDFs: p < .0001 can come out as p , .0001. The engine flags these patterns separately and',
	'method.operatorNoteStrong': 'never uses them for a verdict',
	'method.confTitle': '2 · Confidence score',
	'method.confBody':
		'Every finding carries high / medium / low confidence. Only high and medium findings affect the score and the verdict. Low-confidence findings stay in the list but are marked as',
	'method.confBodyStrong': 'not counted',
	'method.confHigh': 'high',
	'method.confHighCond': 'Explicit statistic + df/n + p, clean operator',
	'method.confHighEffect': 'full penalty',
	'method.confMedium': 'medium',
	'method.confMediumCond': 'Missing p, missing df, comma decimal',
	'method.confMediumEffect': '65% penalty',
	'method.confLow': 'low',
	'method.confLowCond': 'Operator loss, impossible value, ambiguity',
	'method.confLowEffect': 'no penalty',
	'method.confNote':
		'If the document’s overall text quality is low (scanned PDF, broken font), every finding’s confidence is lowered by one level.',
	'method.statsTitle': '3 · Statistics audit',
	'method.statsBody':
		'Expressions such as “t(48) = 2.41, p = .019” are parsed; degrees of freedom and sample size are extracted. The p-value is',
	'method.statsBodyStrong': 'recomputed',
	'method.statsBodyEnd': 'with jStat and compared to the reported value:',
	'method.statsFlip': 'Contradiction — one is p < .05, the other ≥ .05. Critical.',
	'method.statsDev': 'Deviation — same decision, but the difference is large (log₁₀ > 0.3). Warning.',
	'method.statsNoP': 'No p — a statistic is reported without a p-value. Warning.',
	'method.statsOp': 'Operator loss — p , .05. Warning, not counted.',
	'method.statsT': 't → p = 2 · P(T > |t|), df',
	'method.statsF': 'F → p = P(F > f), df₁, df₂',
	'method.statsChi': 'χ² → p = P(χ² > x), df',
	'method.statsR': 'r → t = r√((n−2)/(1−r²))',
	'method.statsZ': 'z → p = 2 · P(Z > |z|)',
	'method.refTitle': '4 · Citations and references',
	'method.refBody':
		'In-text citations (parenthetical and narrative) are matched to reference entries by surname + year. Surnames are compared with Jaro-Winkler similarity (threshold 0.86); Turkish characters are folded. A citation absent from the references is critical; a reference never cited is a warning.',
	'method.langTitle': '5 · Structure and language',
	'method.langBodyA':
		'IMRaD sections, table/figure captions and their in-text references are cross-checked. Causal language (',
	'method.langBodyMonoA': 'caused',
	'method.langBodyB': ', ',
	'method.langBodyMonoB': 'affects',
	'method.langBodyC':
		') is weighed against the study design extracted from the method section: legitimate in an experimental design, an overclaim in a correlational one. If the design cannot be determined, the finding stays at the',
	'method.langBodyEm': 'info',
	'method.langBodyD': 'level.',
	'method.scoreTitle': '6 · Weighted score',
	'method.scoreBody':
		'Category scores start at 100; each decisive finding subtracts by severity (critical 16, warning 7, info 2.5) and confidence weight. To keep dozens of repetitions of the same issue from driving the score to zero, the effect diminishes after six findings. The overall score is the weighted average.',
	'method.scoreThresholds':
		'Thresholds: <strong>A</strong> ≥ 90, <strong>B</strong> ≥ 75, <strong>C</strong> ≥ 60, otherwise <strong>D</strong>.',
	'method.limitsTitle': 'Limitations',
	'method.limit1': 'This tool is <strong>not a peer reviewer</strong>. Findings concern internal consistency; it has no access to raw data or ethics processes.',
	'method.limit2': 'Scanned PDFs (no text layer) cannot be analysed. An OCR version is required.',
	'method.limit3': 'If the reference style is very irregular, entry parsing may be partial; citation results then carry a “low confidence” flag.',
	'method.limit4': 'Statistical expressions are recognised only in standard APA-like patterns; unusual abbreviations may be missed.',
	'method.limit5': 'Language heuristics are context-sensitive; they point, they do not judge.',
	'method.logToggle': 'Analysis log',
	'method.logFile': 'File',
	'method.logFormat': 'Format',
	'method.logPages': 'Pages',
	'method.logChars': 'Characters',
	'method.logQuality': 'Text quality',
	'method.logOperators': 'Operator suspects',
	'method.logBlocks': 'Paragraphs / blocks',
	'method.logCreated': 'Generated',
	'method.logNotes': 'Notes:',
	'method.appendixTitle': 'Finding log',
	'method.appendixBody':
		'The full list of findings, with line-level annotation. Low-confidence findings are marked separately.',
	'method.appendixUndecided': 'excluded from the verdict',

	// --------------------------------------------------------------- viewer
	'viewer.title': 'Document view',
	'viewer.lines': '{n} lines',
	'viewer.pages': '{n} pages',
	'viewer.annotated': '{n} annotated positions',
	'viewer.highlight': 'Highlight:',
	'viewer.countAria': '{n} findings',
	'viewer.flagAria': 'findings on this line',

	// ------------------------------------------------------------- test set
	'testSet.kicker': 'Validation',
	'testSet.title': '25-article test set',
	'testSet.body':
		'The manifest schema lives in src/lib/data/testSet.ts. These titles are fictional placeholders; replace them with your own set. Each article records the main risk it is expected to contain, so you can compare whether the engine caught it.',
	'testSet.inspected': 'inspected',
	'testSet.colArticle': 'Article',
	'testSet.colId': 'ID',
	'testSet.colField': 'Field',
	'testSet.colFocus': 'Focus',
	'testSet.foot': 'Marks are stored only in this browser (localStorage); they are never sent anywhere.',
	'testSet.markAria': 'Mark {id} as inspected',

	// ---------------------------------------------------------------- errors
	'err.unsupported':
		'This file type is not supported. Upload a PDF, DOCX or .tex file. (Legacy .doc files are not supported.)',
	'err.docx': 'The DOCX could not be read: {message}. The file may be corrupt or an old .doc; save it as .docx in Word.',
	'err.docxEmpty': 'No text was found inside the DOCX; the document may contain only images.',
	'err.pdf':
		'The PDF could not be read. The file may be encrypted or corrupt. If it is a scanned PDF, it has no text layer.',
	'err.pdfWorker': 'The PDF worker failed: {message}',
	'err.pdfIncomplete': 'PDF extraction did not complete.',
	'err.empty': 'The file appears to be empty.',
	'err.tooShort': 'At least 100 characters of text are required for analysis.',
	'err.generic': 'Something went wrong while processing the document.',
	'export.title': 'RepCheck report',
	'export.summary': '{critical} critical · {warning} warnings · {undecided} excluded from the verdict',
	'export.colCategory': 'Category',
	'export.colScore': 'Score',
	'export.colWeight': 'Weight',
	'export.colNote': 'Note',
	'export.findingsCount': '{n} findings',
	'export.confidence': 'confidence: {level}',
	'export.footer':
		'This report is an automated pre-submission audit, not a verdict. Low-confidence findings require manual verification.',
	'export.csv.category': 'category',
	'export.csv.severity': 'severity',
	'export.csv.code': 'code',
	'export.csv.title': 'title',
	'export.csv.detail': 'detail',
	'export.csv.confidence': 'confidence',
	'export.csv.decisive': 'counted_in_verdict',
	'export.csv.page': 'page',
	'export.csv.line': 'line',
	'export.csv.quote': 'quote'
};

const tr: Dict = {
	// ---------------------------------------------------------------- common
	'common.beta': 'beta',
	'common.language': 'Dil',
	'common.close': 'Kapat',
	'common.weight': 'ağırlık %{n}',
	'common.page': 's. {n}',
	'common.line': 'satır {n}',
	'common.chars': 'karakter',
	'common.hide': 'Gizle',
	'common.show': 'Göster',
	'common.yes': 'evet',
	'common.no': 'hayır',
	'severity.critical': 'Kritik',
	'severity.warning': 'Uyarı',
	'severity.info': 'Bilgi',
	'severity.ok': 'Tamam',
	'severity.critical.hint': 'Kararı doğrudan etkileyen, güçlü kanıtlı bulgu',
	'severity.warning.hint': 'Düzeltilmesi önerilen raporlama sorunu',
	'severity.info.hint': 'Stil/tercih düzeyinde not',
	'severity.ok.hint': 'Sorun tespit edilmedi',
	'category.statistics': 'İstatistiksel raporlama',
	'category.citations': 'Atıf ve kaynakça',
	'category.structure': 'Yapısal bütünlük',
	'category.language': 'Dil ve iddia düzeyi',
	'category.provenance': 'Çıkarım güvenilirliği',
	'category.short.statistics': 'İstatistik',
	'category.short.citations': 'Atıf',
	'category.short.structure': 'Yapı',
	'category.short.language': 'Dil',
	'category.short.provenance': 'Çıkarım',
	'category.notChecked': 'kontrol edilemedi',
	'category.findings': '{n} bulgu',
	'category.criticals': '{n} kritik',
	'category.noData': 'veri yok',
	'confidence.high': 'Yüksek güven',
	'confidence.medium': 'Orta güven',
	'confidence.low': 'Düşük güven',
	'confidence.high.short': 'yüksek',
	'confidence.medium.short': 'orta',
	'confidence.low.short': 'düşük',
	'design.experimental': 'Deneysel',
	'design.quasi': 'Yarı deneysel',
	'design.correlational': 'İlişkisel / kesitsel',
	'design.case': 'Vaka çalışması',
	'design.unknown': 'Belirlenemedi',
	'time.justNow': 'az önce',
	'time.minutesAgo': '{n} dk önce',
	'time.hoursAgo': '{n} sa önce',
	'reason.decimalComma': 'Ondalık ayırıcı virgülle yazılmış; Türkçe biçim.',
	'reason.pMissing': 'Aynı ifadede raporlanmış p değeri bulunamadı.',
	'reason.dfMissingT': 't için serbestlik derecesi yok; p yeniden hesaplanamaz.',
	'reason.dfMissingF': 'F için serbestlik dereceleri eksik.',
	'reason.dfMissingChi': 'χ² için serbestlik derecesi yok.',
	'reason.nMissing': 'r için örneklem büyüklüğü yok.',
	'reason.negativeT': 'Negatif t değeri; deney/karşılaştırma yönü belirsiz.',
	'reason.impossible': 'Değer olanaklı aralığın dışında.',
	'reason.operatorLoss': 'p ifadesinde operatör kaybı olabilir ("p , .0001").',
	'reason.pOverOne': 'p değeri 1’den büyük okundu.',
	'field.psikoloji': 'psikoloji',
	'field.eğitim': 'eğitim',
	'field.sosyal bilim': 'sosyal bilim',
	'field.sağlık': 'sağlık',

	// --------------------------------------------------------------- landing
	'meta.title': 'RepCheck — makale raporlama denetimi',
	'meta.description':
		'RepCheck; makalelerdeki istatistiksel raporlamayı, atıfları, yapıyı ve iddia dilini tamamen tarayıcınızda denetler.',
	'brand.homeAria': 'RepCheck ana sayfa',
	'nav.paste': 'Metin yapıştır',
	'nav.testSet': 'Test seti',
	'hero.kicker': 'Tarayıcıda çalışır · dosya sunucuya gitmez',
	'hero.titleA': 'Makalenizin',
	'hero.titleEm': 'sayıları',
	'hero.titleB': 'kendi içinde tutarlı mı?',
	'hero.ledeA':
		'RepCheck; PDF, Word veya LaTeX makalenizdeki istatistiksel ifadeleri yeniden hesaplar, metin içi atıfları kaynakçayla eşleştirir, yapıyı ve iddia dilini denetler. Her bulgu',
	'hero.ledeStrong': 'güven skoru',
	'hero.ledeB': 'taşır — düşük güvenli olanlar karara katılmaz.',
	'demo.quickLabel': 'Hemen deneyin:',
	'demo.button': 'Örnek makaleyi analiz et',
	'demo.note': 'hataları bilinçli yerleştirilmiş bir gösterim makalesi',
	'demo.cta': 'Demo ile dene',
	'demo.ownCta': 'Kendi makalenizi deneyin',
	'error.title': 'Belge işlenemedi',
	'error.retry': 'Tekrar dene',
	'error.demo': 'Örnekle devam et',
	'paste.title': 'Metin yapıştır',
	'paste.hint': 'Panoya kopyaladığınız makale metnini yapıştırın. En az 100 karakter gerekir.',
	'paste.fileName': 'yapıştırılan-metin',
	'paste.fileNameTxt': 'yapıştırılan-metin.txt',
	'paste.placeholder': 'Özet\nBu çalışmada ... t(48) = 2.41, p = .019 ...',
	'paste.chars': '{n} karakter',
	'paste.analyze': 'Analiz et',
	'checks.title': 'Beş denetim katmanı',
	'checks.subtitle': 'Her katman ayrı ayrı puanlanır; genel skor ağırlıklı ortalamadır.',
	'checks.weight': 'ağırlık %{n}',
	'checks.stat.title': 'İstatistik',
	'checks.stat.body': 't, F, χ², r ve p ifadelerini ayrıştırır; p değerini jStat ile yeniden hesaplar.',
	'checks.cite.title': 'Atıflar',
	'checks.cite.body': 'Metin içi atıfları kaynakçayla eşleştirir; eksik ve atıfsız kaynakları bulur.',
	'checks.struct.title': 'Yapı',
	'checks.struct.body': 'IMRaD bölümlerini, tablo/şekil altyazılarını ve metin içi atıfları denetler.',
	'checks.lang.title': 'Dil',
	'checks.lang.body': 'Nedensel dili çalışma desenine göre değerlendirir; aşırı iddiaları işaretler.',
	'checks.prov.title': 'Çıkarım',
	'checks.prov.body': 'Taranmış sayfa, bozuk glif ve operatör kaybını raporlar; güveni buna göre ayarlar.',
	'sample.kicker': 'Gösterim',
	'sample.title': 'Hataları yerleştirilmiş örnek makale',
	'sample.bodyA':
		'Sosyal medya ve akademik erteleme üzerine kurgusal bir makale: p çelişkisi, operatör kaybı (',
	'sample.bodyB':
		'), kaynakçada olmayan atıf, atıfsız tablo ve nedensel dil. Sonucun nasıl raporlandığını görün.',
	'sample.open': 'Örneği aç',
	'privacy.title': 'Gizlilik, tasarım gereği',
	'privacy.body':
		'Belge hiçbir ağ isteğiyle dışarı çıkmaz. PDF ayrıştırma, istatistik yeniden hesaplama ve rapor üretimi tamamen tarayıcınızda çalışır. Sekmeyi kapattığınızda bellek temizlenir; kalıcı bir kopya oluşturulmaz.',
	'privacy.li1': 'Sunucu yok, hesap yok, telemetri yok.',
	'privacy.li2': "PDF metni Web Worker'da çıkarılır; arayüz kilitlenmez.",
	'privacy.li3': 'Raporları HTML / CSV / JSON olarak indirebilirsiniz.',
	'footer.tagline': 'RepCheck · istemci taraflı ön denetim aracı',
	'footer.note': 'Kesin hakem kararı değildir; düşük güvenli bulgular manuel doğrulanmalıdır.',

	// -------------------------------------------------------------- dropzone
	'drop.aria': 'Makale dosyası yükle: PDF, DOCX veya LaTeX',
	'drop.headline': 'Makaleyi buraya bırakın',
	'drop.subBefore': 'veya',
	'drop.subLink': 'dosya seçin',
	'drop.subAfter': '— PDF, Word (.docx), LaTeX (.tex) veya düz metin',
	'drop.limit': 'en fazla 40 MB',
	'drop.errorUnsupported':
		'Bu dosya türü desteklenmiyor. PDF, DOCX, .tex veya .txt yükleyin. (.doc uzantılı eski Word dosyaları Word’de .docx olarak kaydedilmelidir.)',
	'drop.errorSize': 'Dosya 40 MB’tan büyük. Daha küçük bir dosya deneyin.',
	'drop.errorEmpty': 'Dosya boş görünüyor.',

	// ------------------------------------------------------------ processing
	'proc.file': 'Metin çıkarımı',
	'proc.analyze': 'Motorlar çalışıyor',
	'proc.report': 'Rapor',
	'proc.local': 'tarayıcıda, sunucusuz',
	'proc.evaluating': 'bulgular değerlendiriliyor',
	'proc.pageOf': 'sayfa {page} / {total}',
	'proc.stage.starting': 'Başlıyor',
	'proc.stage.preparing': 'Dosya hazırlanıyor',
	'proc.stage.pdfReading': 'PDF okunuyor',
	'proc.stage.pdfOpened': 'PDF açıldı',
	'proc.stage.page': 'Sayfa {page}/{total} okunuyor',
	'proc.stage.workerFallback': 'Worker kullanılamıyor, ana thread’de deneniyor',
	'proc.stage.docxLoader': 'DOCX okuyucusu yükleniyor',
	'proc.stage.docxReading': 'DOCX okunuyor',
	'proc.stage.paragraphs': 'Paragraflar çıkarılıyor',
	'proc.stage.latexRead': 'LaTeX kaynağı okunuyor',
	'proc.stage.latexParse': 'LaTeX ayrıştırılıyor',
	'proc.stage.textRead': 'Metin okunuyor',
	'proc.stage.analyzing': 'Metin analiz ediliyor',
	'proc.stage.report': 'Rapor hazırlanıyor',
	'proc.stage.demoPreparing': 'Örnek makale hazırlanıyor',
	'proc.stage.done': 'Tamamlandı',

	// ----------------------------------------------------------- report shell
	'report.back': 'Yeni belge',
	'report.quality': 'çıkarım güvenilirliği: {level}',
	'report.qualityHigh': 'yüksek',
	'report.qualityMedium': 'orta',
	'report.qualityLow': 'düşük',
	'report.exportHtml': 'HTML raporu',
	'report.tab.overview': 'Özet',
	'report.tab.findings': 'Bulgular',
	'report.tab.stats': 'İstatistik',
	'report.tab.refs': 'Atıflar',
	'report.tab.structure': 'Yapı & dil',
	'report.tab.method': 'Yöntem',
	'report.hideText': 'Metni gizle',
	'report.showText': 'Metni göster',
	'report.charsK': '{n} bin karakter',
	'report.tabsAria': 'Rapor bölümleri',

	// -------------------------------------------------------------- overview
	'overview.eyebrow': 'Genel değerlendirme',
	'overview.critical': '{n} kritik',
	'overview.warning': '{n} uyarı',
	'overview.info': '{n} bilgi',
	'overview.undecided':
		'{n} bulgu düşük güven nedeniyle karara dahil edilmedi; bunlar manuel doğrulama gerektirir.',
	'overview.verdict.clean':
		'<strong>Raporlama büyük ölçüde tutarlı.</strong> Kritik bir çelişki bulunmadı; yalnızca küçük iyileştirmeler önerilir.',
	'overview.verdict.critical':
		'<strong>{n} kritik bulgu</strong> istatistiksel raporlamanın veya kaynakçanın doğrulanmasını gerektiriyor. Bu bulgular ham veriyle teyit edilmeden yayına gönderilmemelidir.',
	'overview.verdict.good':
		'<strong>Genel olarak iyi.</strong> Birkaç raporlama boşluğu var; düzeltmeler hızlıca yapılabilir.',
	'overview.verdict.medium':
		'<strong>Orta düzeyde sorunlar.</strong> Birden fazla uyarı bir arada; raporlama standardı gözden geçirilmeli.',
	'overview.verdict.bad':
		'<strong>Ciddi raporlama sorunları.</strong> Genel skor {score} (not {grade}); bulguların tek tek ele alınması gerekir.',
	'overview.qualityEyebrow': 'Metin çıkarımı güvenilirliği',
	'overview.qualityOk': 'Metin katmanı sağlıklı görünüyor; bulgular doğrudan metne dayanıyor.',
	'overview.categoriesTitle': 'Kategori skorları',
	'overview.categoriesSub': 'Skor, yalnızca yüksek/orta güvenli bulgulardan düşülerek hesaplanır.',
	'overview.priorityTitle': 'Öncelikli bulgular',
	'overview.prioritySub': 'Karara dahil edilen en önemli {n} bulgu.',
	'overview.priorityNone': 'Karara dahil edilen kritik veya uyarı düzeyinde bulgu yok.',
	'overview.coverageTitle': 'Kapsam',
	'overview.covStats': 'İstatistik ifadesi',
	'overview.covStatsNote': '{consistent} tutarlı · {inconsistent} çelişkili',
	'overview.covCitations': 'Metin içi atıf',
	'overview.covCitationsNote': '{matched} eşleşti · {missing} eksik',
	'overview.covReferences': 'Kaynakça girdisi',
	'overview.covReferencesNote': '{orphans} atıfsız',
	'overview.covFigures': 'Tablo / şekil',
	'overview.covFiguresNote': '{unreferenced} atıfsız',
	'overview.covLanguage': 'Dilsel işaret',
	'overview.covLanguageNote': '{causal} gerekçesiz nedensel',
	'overview.design': 'Çalışma deseni:',

	// -------------------------------------------------------------- findings
	'findings.all': 'Tümü',
	'findings.category': 'Kategori',
	'findings.searchLabel': 'Ara',
	'severity.label': 'Önem düzeyi',
	'findings.critical': 'Kritik',
	'findings.warning': 'Uyarı',
	'findings.info': 'Bilgi',
	'findings.categoryAll': 'Tüm kategoriler',
	'findings.search': 'Bulgu ara…',
	'findings.includeUndecided': 'Karara dahil edilmeyenleri de göster',
	'findings.showing': '{n} bulgu gösteriliyor',
	'findings.showingUndecided': ' · {n} tanesi karara dahil değil',
	'findings.empty': 'Bu filtreyle eşleşen bulgu yok.',
	'findings.none': 'Bulgu yok.',
	'findings.legend': 'Önem düzeyi açıklaması',
	'findings.legendText': '{label}: {hint}',

	// ------------------------------------------------------------- issue card
	'issue.notDecisive': 'karara dahil değil',
	'issue.suggestion': 'Öneri',
	'issue.showInText': 'Metinde göster',
	'issue.confidenceTitle': 'Güven: {level}',
	'issue.lowConfidence': 'düşük güven',

	// ----------------------------------------------------------------- stats
	'stats.total': 'istatistik ifadesi',
	'stats.consistent': 'tutarlı',
	'stats.inconsistent': 'çelişki / sapma',
	'stats.unverifiable': 'doğrulanamadı',
	'stats.empty':
		'Raporlanmış istatistik ifadesi bulunamadı. Metinde t(48) = 2.41, p = .019 gibi ifadeler aranır.',
	'stats.emptyMono': 't(48) = 2.41, p = .019',
	'stats.col.expression': 'İfade',
	'stats.col.value': 'Değer',
	'stats.col.df': 'df / n',
	'stats.col.reported': 'Raporlanan p',
	'stats.col.computed': 'Hesaplanan p',
	'stats.col.status': 'Durum',
	'stats.col.confidence': 'Güven',
	'stats.status.consistent': 'Tutarlı',
	'stats.status.flip': 'Çelişki',
	'stats.status.deviation': 'Sapma',
	'stats.status.unverifiable': 'Doğrulanamadı',
	'stats.status.uncomputable': 'Hesaplanamadı',
	'stats.delta': 'Fark:',
	'stats.boundNote': 'raporlanan bir sınır (p < .001)',
	'stats.showInText': 'Metinde göster',
	'stats.claimsTitle': 'Anlamlılık iddiaları',
	'stats.claimsHint': '"Anlamlı" / "significant" ifadelerinin aynı cümlede sayısal destek içerip içermediği.',
	'stats.supported': 'destekli',
	'stats.unsupported': 'desteksiz',
	'stats.negated': 'olumsuz',
	'stats.methodTitle': 'p değeri nasıl yeniden hesaplanıyor?',
	'stats.methodT': 't → Student t dağılımı, df = t(·) içindeki değer, iki yönlü.',
	'stats.methodF': 'F → merkezi F dağılımı, F(df1, df2), üst kuyruk.',
	'stats.methodChi': 'χ² → ki-kare dağılımı, χ²(df), üst kuyruk.',
	'stats.methodR': 'r → t = r√((n−2)/(1−r²)), df = n−2, iki yönlü.',
	'stats.methodZ': 'z → standart normal dağılım, iki yönlü.',
	'stats.tolerance':
		'Tolerans: log₁₀ ölçeğinde 0.3 birim (yaklaşık 2 kat) veya mutlak 0.005. Bunun ötesindeki farklar "sapma"; anlamlılık eşiğini geçen farklar "çelişki" olarak işaretlenir. Düşük güvenli ifadelerde çelişki bile karar olarak raporlanmaz.',
	'stats.notComputable': 'yeniden hesaplanamadı',
	'stats.twoTailed': 'iki yönlü',
	'stats.oneTailed': 'tek yönlü',
	'stats.pShort': 'p = {p}',

	// ------------------------------------------------------------ references
	'refs.citations': 'metin içi atıf',
	'refs.references': 'kaynakça girdisi',
	'refs.matched': 'eşleşen',
	'refs.missing': 'kaynakçada yok',
	'refs.orphans': 'atıf verilmeyen',
	'refs.noSectionTitle': 'Kaynakça bölümü tespit edilemedi.',
	'refs.noSectionBody':
		'"Kaynakça", "Kaynaklar" veya "References" başlığı bulunamadığı için atıf çapraz kontrolü yapılamadı.',
	'refs.noSectionCitations': 'Metinde {n} atıf var; bunlar doğrulanamadı.',
	'refs.tab.matches': 'Eşleşmeler',
	'refs.tab.missing': 'Eksik / uyuşmaz',
	'refs.tab.orphans': 'Atıfsız kaynaklar',
	'refs.col.citation': 'Metin içi atıf',
	'refs.col.status': 'Durum',
	'refs.col.match': 'Kaynakça eşleşmesi',
	'refs.status.matched': 'eşleşti',
	'refs.status.missing': 'kaynakçada yok',
	'refs.status.year': 'yıl uyuşmuyor',
	'refs.status.author': 'yazar uyuşmuyor',
	'refs.similar': 'Benzer girdiler:',
	'refs.emptyMatches': 'Metin içi atıf bulunamadı.',
	'refs.emptyMissing': 'Tüm metin içi atıflar kaynakçayla eşleşiyor.',
	'refs.emptyOrphans': 'Kaynakçadaki tüm girdilere metinde atıf verilmiş.',
	'refs.uncited': 'metinde atıf yok',
	'refs.showInText': 'Metinde göster',
	'refs.etal': 'vd.',
	'refs.viewAria': 'Atıf görünümü',
	'refs.yearArrow': 'yıl: {from} → {to}',
	'refs.methodTitle': 'Eşleştirme nasıl çalışıyor?',
	'refs.methodBody':
		'Metin içi atıflar (Yılmaz, 2019; (Smith & Jones, 2020); Öztürk vd., 2021) yazar soyadı + yıl anahtarına indirgenir. Kaynakça girdileri aynı şekilde ayrıştırılır. Soyadları Jaro-Winkler benzerliğiyle (eşik 0.86) karşılaştırılır; yıl birebir eşleşmezse "yıl uyuşmazlığı", yazar da eşleşmezse "yazar uyuşmazlığı" raporlanır. Eşleşmeyen atıflar kritik, kullanılmayan kaynaklar uyarı olarak listelenir.',

	// ------------------------------------------------------------- structure
	'struct.sectionsTitle': 'Bölüm haritası',
	'struct.sectionsSub': 'IMRaD yapısı aranır; başlıklar numaralı veya düz metin olabilir.',
	'struct.section.abstract': 'Öz',
	'struct.section.keywords': 'Anahtar kelimeler',
	'struct.section.introduction': 'Giriş',
	'struct.section.method': 'Yöntem',
	'struct.section.results': 'Bulgular',
	'struct.section.discussion': 'Tartışma',
	'struct.section.references': 'Kaynakça',
	'struct.section.appendix': 'Ekler',
	'struct.found': 'bulundu',
	'struct.notFound': 'bulunamadı',
	'struct.detail': 'Detay',
	'struct.gaps': 'Numaralandırma boşlukları:',
	'struct.figuresTitle': 'Tablo ve şekiller',
	'struct.figuresSub': 'Altyazı ↔ metin içi atıf çapraz kontrolü.',
	'struct.tables': 'Tablolar',
	'struct.figures': 'Şekiller',
	'struct.tableNo': 'Tablo {n}',
	'struct.figureNo': 'Şekil {n}',
	'struct.referenced': 'atıflı',
	'struct.unreferenced': 'atıfsız',
	'struct.noFigures': 'Tablo veya şekil altyazısı bulunamadı.',
	'struct.dangling': 'Metinde atıf var ama altyazı yok:',
	'struct.languageTitle': 'Dil ve iddia düzeyi',
	'struct.languageSub':
		'Nedensel dil, aşırı iddia ve test edilmemiş anlamlılık ifadeleri. Çalışma deseni:',
	'struct.designEvidence': 'kanıt:',
	'struct.emptyLang': 'Dikkat çeken dilsel kalıp bulunamadı.',
	'struct.lang.causal': 'nedensel',
	'struct.lang.causalJustified': 'nedensel (desen destekliyor)',
	'struct.lang.absolutist': 'aşırı iddia',
	'struct.lang.generalization': 'genelleme',
	'struct.lang.significance': 'desteksiz anlamlılık',

	// -------------------------------------------------------------- method
	'method.title': 'Nasıl çalışıyor?',
	'method.leadA': 'RepCheck, makaleyi',
	'method.leadStrong': 'yalnızca tarayıcınızda',
	'method.leadB':
		'işler. Dosya hiçbir sunucuya gönderilmez; sayfa yenilendiğinde bellekten silinir. Amaç, yayın öncesi bir editörün yakalayabileceği raporlama tutarsızlıklarını hızlıca görünür kılmaktır — kesin bir hakem kararı değil,',
	'method.leadEm': 'ön denetim',
	'method.pipe.extract': 'Çıkarım',
	'method.pipe.parse': 'Ayrıştırma',
	'method.pipe.cross': 'Çapraz kontrol',
	'method.pipe.confidence': 'Güven filtresi',
	'method.pipe.score': 'Skor + annotasyon',
	'method.pipelineAria': 'İşlem hattı',
	'method.confColLevel': 'Güven',
	'method.confColCond': 'Tipik koşul',
	'method.confColEffect': 'Skora etkisi',
	'method.extractTitle': '1 · Metin çıkarımı',
	'method.extractPdf': "PDF — pdf.js, ayrı bir Web Worker'da, sayfa sayfa. Her metin satırının bounding box'ı saklanır.",
	'method.extractDocx': 'DOCX — mammoth.js ham paragraf metni üretir; sayfa geometrisi yoktur.',
	'method.extractTex': 'LaTeX — kaynak doğrudan ayrıştırılır; bölüm, tablo/şekil ve kaynakça yapısı en güvenilir burada çıkar.',
	'method.operatorNote':
		"PDF'te operatör kaybı gerçek bir sorundur: p < .0001 çıkarımda p , .0001 olabilir. Motor bu kalıpları ayrıca işaretler ve",
	'method.operatorNoteStrong': 'karar için kullanmaz',
	'method.confTitle': '2 · Güven skoru',
	'method.confBody':
		'Her bulgu yüksek / orta / düşük güven taşır. Kural: yalnızca yüksek ve orta güvenli bulgular skoru ve "hata" kararını etkiler. Düşük güvenli bulgular listede kalır ama',
	'method.confBodyStrong': 'karara dahil edilmez',
	'method.confHigh': 'yüksek',
	'method.confHighCond': 'Açık istatistik + df/n + p, net operatör',
	'method.confHighEffect': 'tam ceza',
	'method.confMedium': 'orta',
	'method.confMediumCond': 'p eksik, df eksik, virgüllü ondalık',
	'method.confMediumEffect': '%65 ceza',
	'method.confLow': 'düşük',
	'method.confLowCond': 'Operatör kaybı, olanaksız değer, belirsizlik',
	'method.confLowEffect': 'ceza yok',
	'method.confNote':
		'Belgenin genel metin kalitesi düşükse (taranmış PDF, bozuk font) tüm bulguların güveni bir kademe düşürülür.',
	'method.statsTitle': '3 · İstatistik denetimi',
	'method.statsBody':
		'Metindeki t(48) = 2.41, p = .019 gibi ifadeler ayrıştırılır; serbestlik dereceleri ve örneklem büyüklüğü çıkarılır. p değeri jStat ile',
	'method.statsBodyStrong': 'yeniden hesaplanır',
	'method.statsBodyEnd': 've raporlanan değerle karşılaştırılır:',
	'method.statsFlip': 'Çelişki — biri p < .05, diğeri ≥ .05. Kritik bulgu.',
	'method.statsDev': 'Sapma — ikisi de aynı kararda ama fark büyük (log₁₀ > 0.3). Uyarı.',
	'method.statsNoP': 'p yok — istatistik var, p raporlanmamış. Uyarı.',
	'method.statsOp': 'Operatör kaybı — p , .05. Uyarı, karar dışı.',
	'method.statsT': 't → p = 2 · P(T > |t|), df',
	'method.statsF': 'F → p = P(F > f), df₁, df₂',
	'method.statsChi': 'χ² → p = P(χ² > x), df',
	'method.statsR': 'r → t = r√((n−2)/(1−r²))',
	'method.statsZ': 'z → p = 2 · P(Z > |z|)',
	'method.refTitle': '4 · Atıf ve kaynakça',
	'method.refBody':
		'Metin içi atıflar (parantez içi ve anlatı biçimi) ile kaynakça girdileri yazar soyadı + yıl anahtarıyla eşleştirilir. Soyadları Jaro-Winkler benzerliğiyle (eşik 0.86) karşılaştırılır; Türkçe karakterler sadeleştirilir. Kaynakçada olmayan atıf kritik, metinde atıf verilmeyen kaynak uyarıdır.',
	'method.langTitle': '5 · Yapı ve dil',
	'method.langBodyA':
		'IMRaD bölümleri, tablo/şekil altyazıları ve metin içi atıflar çapraz kontrol edilir. Nedensel dil (',
	'method.langBodyMonoA': 'neden oldu',
	'method.langBodyB': ', ',
	'method.langBodyMonoB': 'etkiledi',
	'method.langBodyC':
		') yöntem bölümünden çıkarılan çalışma desenine göre değerlendirilir: deneysel desende meşru, ilişkisel desende aşırı iddia. Desen belirlenemezse bulgu',
	'method.langBodyEm': 'bilgi',
	'method.langBodyD': 'düzeyinde kalır.',
	'method.scoreTitle': '6 · Ağırlıklı skor',
	'method.scoreBody':
		'Kategori skorları 100’den başlar; her kararlı bulgu, önem düzeyine (kritik 16, uyarı 7, bilgi 2.5) ve güven katsayısına göre düşülür. Aynı sorunun onlarca tekrarı skoru sıfıra sürüklemesin diye 6’dan fazla bulguda azalan etki uygulanır. Genel skor, ağırlıklı ortalamadır.',
	'method.scoreThresholds':
		'Eşikler: <strong>A</strong> ≥ 90, <strong>B</strong> ≥ 75, <strong>C</strong> ≥ 60, aksi halde <strong>D</strong>.',
	'method.limitsTitle': 'Sınırlar',
	'method.limit1': 'Bu araç <strong>hakem değildir</strong>. Bulgular metnin kendi içindeki tutarlılığa bakar; ham veriye veya etik kurul süreçlerine erişemez.',
	'method.limit2': "Taranmış (metin katmanı olmayan) PDF'ler analiz edilemez. OCR uygulanmış sürüm gerekir.",
	'method.limit3': 'Kaynakça biçimi çok düzensizse girdi ayrıştırması kısmi kalabilir; bu durumda atıf sonuçları "düşük güven" işareti taşır.',
	'method.limit4': 'İstatistik ifadeleri yalnızca standart APA benzeri kalıplarla tanınır; özel kısaltmalar gözden kaçabilir.',
	'method.limit5': 'Dil heuristikleri bağlam duyarlıdır; kesin yargı üretmez, işaret eder.',
	'method.logToggle': 'Analiz kaydı',
	'method.logFile': 'Dosya',
	'method.logFormat': 'Biçim',
	'method.logPages': 'Sayfa',
	'method.logChars': 'Karakter',
	'method.logQuality': 'Metin kalitesi',
	'method.logOperators': 'Operatör şüphesi',
	'method.logBlocks': 'Paragraf / blok',
	'method.logCreated': 'Oluşturulma',
	'method.logNotes': 'Notlar:',
	'method.appendixTitle': 'Bulgu dökümü',
	'method.appendixBody':
		'Raporun tam listesi; satır bazlı annotasyonla birlikte. Düşük güvenli bulgular ayrı işaretlidir.',
	'method.appendixUndecided': 'karara dahil edilmeyenler',

	// --------------------------------------------------------------- viewer
	'viewer.title': 'Belge görünümü',
	'viewer.lines': '{n} satır',
	'viewer.pages': '{n} sayfa',
	'viewer.annotated': '{n} işaretli konum',
	'viewer.highlight': 'Vurgu:',
	'viewer.countAria': '{n} bulgu',
	'viewer.flagAria': 'bu satırdaki bulgular',

	// ------------------------------------------------------------- test set
	'testSet.kicker': 'Doğrulama',
	'testSet.title': '25 makalelik test seti',
	'testSet.body':
		'Manifest şeması src/lib/data/testSet.ts dosyasında. Buradaki başlıklar kurgusal örnektir; kendi veri setinizle değiştirin. Her makale için beklenen asıl risk not edilmiştir — böylece motorun o riski yakalayıp yakalamadığı karşılaştırılabilir.',
	'testSet.inspected': 'denetlendi',
	'testSet.colArticle': 'Makale',
	'testSet.colId': 'Kod',
	'testSet.colField': 'Alan',
	'testSet.colFocus': 'Odak',
	'testSet.foot': "İşaretlemeler yalnızca bu tarayıcıda saklanır (localStorage); dışarı gönderilmez.",
	'testSet.markAria': '{id} denetlendi olarak işaretle',

	// ---------------------------------------------------------------- errors
	'err.unsupported':
		'Desteklenmeyen dosya türü. PDF, DOCX veya .tex dosyası yükleyin. (Eski .doc formatı desteklenmez.)',
	'err.docx': 'DOCX okunamadı: {message}. Dosya bozuk olabilir veya .doc (eski format) olabilir; Word’de .docx olarak kaydedin.',
	'err.docxEmpty': 'DOCX içinde metin bulunamadı; belge yalnızca görsel içeriyor olabilir.',
	'err.pdf':
		'PDF okunamadı. Dosya şifreli veya bozuk olabilir. Taranmış PDF ise metin katmanı yoktur.',
	'err.pdfWorker': 'PDF worker hatası: {message}',
	'err.pdfIncomplete': 'PDF çıkarımı tamamlanamadı.',
	'err.empty': 'Dosya boş görünüyor.',
	'err.tooShort': 'Analiz için en az 100 karakterlik metin gerekli.',
	'err.generic': 'Belge işlenirken bir sorun oluştu.',
	'export.title': 'RepCheck raporu',
	'export.summary': '{critical} kritik · {warning} uyarı · {undecided} karara dahil değil',
	'export.colCategory': 'Kategori',
	'export.colScore': 'Skor',
	'export.colWeight': 'Ağırlık',
	'export.colNote': 'Not',
	'export.findingsCount': '{n} bulgu',
	'export.confidence': 'güven: {level}',
	'export.footer':
		'Bu rapor otomatik bir ön denetimdir; kesin yargı yerine geçmez. Düşük güvenli bulgular manuel doğrulanmalıdır.',
	'export.csv.category': 'kategori',
	'export.csv.severity': 'önem',
	'export.csv.code': 'kod',
	'export.csv.title': 'başlık',
	'export.csv.detail': 'açıklama',
	'export.csv.confidence': 'güven',
	'export.csv.decisive': 'karara_dahil',
	'export.csv.page': 'sayfa',
	'export.csv.line': 'satır',
	'export.csv.quote': 'alıntı'
};

export const DICT: Record<'en' | 'tr', Dict> = { en, tr };
